import { useDataEngine } from "@dhis2/app-runtime";

export const CONFIG = {
  program: "RSANdetbnWC",
  trackedEntityType: "OaAbpIs1cV7",
  stages: {
    config: "RF0WnJmJczX",
    weights: "zY4mj9a30tk",
    midYear: "mJ3mC8f2eMv",
    endYear: "TiLy9LFPaNo",
    approvals: "zC1XdAhrAhr",
    managerReview: "H8EXv8f2vif",
    staffFinalization: "OAgW7UY08ib",
  },
  de: {
    // Competency config data elements
    competencyId: "hM7qEqQBciM",
    competencyCode: "KekX9kvB4b2",
    competencyDefinition: "n9DVY0rbIsl",
    weightValue: "NnO0P1bqPGw",
    
    // Sort order data element
    sortOrder: "UYl8sCxJmXa",
    
    // Rating data elements
    ratingScore: "fPIRWdClNPe",
    ratingComment: "SVFdFerDLV1",
    
    // Approval data elements
    approvalStatus: "iNiXoYwyNUL",
    approvalRemark: "s7aNZvcg8pH",
    
    // Manager Review data elements
    managerRatingScore: "R15VjbLnIzh",
    managerRatingComment: "d1c68osn30T",
    periodType: "Bi2Gs8Rpr2v",
    
    // Staff Finalization data elements
    staffAgreementStatus: "pthfmZuemRP",
    staffAgreementComment: "MMqpbBpfqN3",
    finalApprovalStatus: "r7yqOINMJEU",
    
    // Common references
    positionReference: "SlgO60cMK3F"
  },
};

export function useLeadershipServices() {
  const engine = useDataEngine();

  // --- CREATE Event ---
  const createEvent = async (eventData) => {
    const query = {
      resource: "events",
      type: "create",
      data: eventData,
    };
    return await engine.mutate(query);
  };

  // --- READ Events ---
  const getEvents = async ({ program, trackedEntityInstance, orgUnit, programStage }) => {
    const params = {
      program,
      trackedEntityInstance,
      orgUnit,
      fields: "event,programStage,eventDate,dataValues[dataElement,value]",
      pageSize: 200,
    };
    if (programStage) {
      params.programStage = programStage;
    }
    const query = {
      events: {
        resource: "events",
        params: params,
      },
    };
    const result = await engine.query(query);
    return result.events?.events || [];
  };

  // --- UPDATE Event ---
  const updateEvent = async (eventId, updatedData) => {
    const query = {
      resource: `events/${eventId}`,
      type: "update",
      data: updatedData,
    };
    return await engine.mutate(query);
  };

  // --- DELETE Event ---
  const deleteEvent = async (eventId) => {
    const query = {
      resource: `events/${eventId}`,
      type: "delete",
    };
    return await engine.mutate(query);
  };

  // --- Create Competency Config Event ---
  const createCompetencyConfig = async (teiId, orgUnitId, enrollmentId, competency, sortOrder = 0) => {
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competency.competencyId || `${Date.now()}` },
      { dataElement: CONFIG.de.competencyCode, value: competency.name },
      { dataElement: CONFIG.de.competencyDefinition, value: competency.definition || competency.name },
      // Save sort order
      { dataElement: CONFIG.de.sortOrder, value: sortOrder.toString() },
    ];
    
    if (competency.weight !== null && competency.weight !== undefined) {
      dataValues.push({ dataElement: CONFIG.de.weightValue, value: competency.weight.toString() });
    }
    
    return createEvent({
      program: CONFIG.program,
      programStage: CONFIG.stages.config,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      enrollment: enrollmentId,
      eventDate: new Date().toISOString().split('T')[0],
      status: "COMPLETED",
      dataValues: dataValues,
    });
  };

  // --- Update Competency Config Event ---
  const updateCompetencyConfig = async (eventId, teiId, orgUnitId, enrollmentId, competency, sortOrder) => {
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competency.competencyId },
      { dataElement: CONFIG.de.competencyCode, value: competency.name },
      { dataElement: CONFIG.de.competencyDefinition, value: competency.definition || competency.name },
    ];
    
    if (competency.weight !== null && competency.weight !== undefined) {
      dataValues.push({ dataElement: CONFIG.de.weightValue, value: competency.weight.toString() });
    }
    
    // Save sort order if provided
    if (sortOrder !== undefined && sortOrder !== null) {
      dataValues.push({ dataElement: CONFIG.de.sortOrder, value: sortOrder.toString() });
    }
    
    return updateEvent(eventId, {
      event: eventId,
      program: CONFIG.program,
      programStage: CONFIG.stages.config,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      enrollment: enrollmentId,
      eventDate: new Date().toISOString().split('T')[0],
      status: "COMPLETED",
      dataValues: dataValues,
    });
  };

  // --- NEW: Bulk save competency configs with sort order ---
  const saveCompetencyConfigsBulk = async (teiId, orgUnitId, enrollmentId, competencies) => {
    const results = [];
    
    for (let i = 0; i < competencies.length; i++) {
      const comp = competencies[i];
      if (!comp.name?.trim()) continue;
      
      if (comp.eventId) {
        // Update existing
        const result = await updateCompetencyConfig(
          comp.eventId,
          teiId,
          orgUnitId,
          enrollmentId,
          comp,
          i // Pass index as sort order
        );
        results.push({ ...comp, sortOrder: i, result });
      } else {
        // Create new
        const result = await createCompetencyConfig(
          teiId,
          orgUnitId,
          enrollmentId,
          comp,
          i // Pass index as sort order
        );
        const eventId = result.response?.reference || result.response?.uid;
        results.push({ ...comp, eventId, competencyId: eventId, sortOrder: i, result });
      }
    }
    
    return results;
  };

  // --- Save Rating for Competency (Staff Self-Rating) ---
  const saveRating = async (teiId, orgUnitId, enrollmentId, competencyId, ratingIndex, ratingValue, comment) => {
    const programStage = ratingIndex === 0 ? CONFIG.stages.midYear : CONFIG.stages.endYear;
    
    const existingEvents = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: programStage,
    });
    
    const existingEvent = existingEvents.find(event => {
      const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
      return idDV?.value === competencyId;
    });
    
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competencyId },
      { dataElement: CONFIG.de.ratingScore, value: ratingValue.toString() },
      { dataElement: CONFIG.de.ratingComment, value: comment || "" },
    ];
    
    if (existingEvent) {
      return updateEvent(existingEvent.event, {
        event: existingEvent.event,
        program: CONFIG.program,
        programStage: programStage,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    } else {
      return createEvent({
        program: CONFIG.program,
        programStage: programStage,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    }
  };

  // --- Save Manager Rating ---
  const saveManagerRating = async (teiId, orgUnitId, enrollmentId, competencyId, period, ratingValue, comment) => {
    const existingEvents = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: CONFIG.stages.managerReview,
    });
    
    const existingEvent = existingEvents.find(event => {
      const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
      return idDV?.value === competencyId;
    });
    
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competencyId },
      { dataElement: CONFIG.de.managerRatingScore, value: ratingValue.toString() },
      { dataElement: CONFIG.de.managerRatingComment, value: comment || "" },
      { dataElement: CONFIG.de.periodType, value: period === 0 ? 'mid_year' : 'end_year' },
    ];
    
    if (existingEvent) {
      return updateEvent(existingEvent.event, {
        event: existingEvent.event,
        program: CONFIG.program,
        programStage: CONFIG.stages.managerReview,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    } else {
      return createEvent({
        program: CONFIG.program,
        programStage: CONFIG.stages.managerReview,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    }
  };

  // --- Save Staff Finalization ---
  const saveStaffFinalization = async (teiId, orgUnitId, enrollmentId, competencyId, period, agrees, comment) => {
    const existingEvents = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: CONFIG.stages.staffFinalization,
    });
    
    const existingEvent = existingEvents.find(event => {
      const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
      return idDV?.value === competencyId;
    });
    
    const agreementValue = agrees ? "true" : "false";
    const finalizationStatus = "finalized";
    
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competencyId },
      { dataElement: CONFIG.de.staffAgreementStatus, value: agreementValue },
      { dataElement: CONFIG.de.staffAgreementComment, value: comment || "" },
      { dataElement: CONFIG.de.finalApprovalStatus, value: finalizationStatus },
      { dataElement: CONFIG.de.periodType, value: period === 0 ? 'mid_year' : 'end_year' },
    ];
    
    if (existingEvent) {
      return updateEvent(existingEvent.event, {
        event: existingEvent.event,
        program: CONFIG.program,
        programStage: CONFIG.stages.staffFinalization,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    } else {
      return createEvent({
        program: CONFIG.program,
        programStage: CONFIG.stages.staffFinalization,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    }
  };

  // --- Get Staff Finalizations ---
  const getStaffFinalizations = async (teiId, orgUnitId) => {
    const events = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: CONFIG.stages.staffFinalization,
    });
    
    const finalizationsMap = {};
    events.forEach(event => {
      const dataValuesMap = {};
      event.dataValues?.forEach(dv => {
        dataValuesMap[dv.dataElement] = dv.value;
      });
      
      const competencyId = dataValuesMap[CONFIG.de.competencyId];
      const period = dataValuesMap[CONFIG.de.periodType] === 'mid_year' ? 0 : 1;
      
      if (!finalizationsMap[competencyId]) {
        finalizationsMap[competencyId] = { 
          staffAgreement: [null, null], 
          staffAgreementComment: ["", ""], 
          finalApprovalStatus: null
        };
      }
      
      const agreementValue = dataValuesMap[CONFIG.de.staffAgreementStatus];
      finalizationsMap[competencyId].staffAgreement[period] = agreementValue === "true";
      finalizationsMap[competencyId].staffAgreementComment[period] = dataValuesMap[CONFIG.de.staffAgreementComment] || "";
      finalizationsMap[competencyId].finalApprovalStatus = dataValuesMap[CONFIG.de.finalApprovalStatus] || null;
    });
    
    return finalizationsMap;
  };

  // --- Save Approval for Competency ---
  const saveApproval = async (teiId, orgUnitId, enrollmentId, competencyId, status, remark) => {
    const existingEvents = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: CONFIG.stages.approvals,
    });
    
    const existingEvent = existingEvents.find(event => {
      const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
      return idDV?.value === competencyId;
    });
    
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competencyId },
      { dataElement: CONFIG.de.approvalStatus, value: status },
      { dataElement: CONFIG.de.approvalRemark, value: remark || "" },
    ];
    
    if (existingEvent) {
      return updateEvent(existingEvent.event, {
        event: existingEvent.event,
        program: CONFIG.program,
        programStage: CONFIG.stages.approvals,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    } else {
      return createEvent({
        program: CONFIG.program,
        programStage: CONFIG.stages.approvals,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    }
  };

  // --- Get All Competency Configs (WITH SORT ORDER) ---
  const getCompetencyConfigs = async (orgUnitId) => {
    const events = await getEvents({
      program: CONFIG.program,
      programStage: CONFIG.stages.config,
      orgUnit: orgUnitId,
    });
    
    const configs = events.map(event => {
      const dataValuesMap = {};
      event.dataValues?.forEach(dv => {
        dataValuesMap[dv.dataElement] = dv.value;
      });
      
      return {
        eventId: event.event,
        competencyId: dataValuesMap[CONFIG.de.competencyId],
        name: dataValuesMap[CONFIG.de.competencyCode] || "",
        definition: dataValuesMap[CONFIG.de.competencyDefinition] || "",
        weight: dataValuesMap[CONFIG.de.weightValue] ? parseFloat(dataValuesMap[CONFIG.de.weightValue]) : null,
        weightLocked: !!dataValuesMap[CONFIG.de.weightValue],
        // Read sort order
        sortOrder: dataValuesMap[CONFIG.de.sortOrder] !== undefined 
          ? parseInt(dataValuesMap[CONFIG.de.sortOrder]) 
          : null,
      };
    }).filter(c => c.name);
    
    // Sort by sortOrder (items with null sortOrder go to the end, preserving event order)
    configs.sort((a, b) => {
      if (a.sortOrder === null && b.sortOrder === null) return 0;
      if (a.sortOrder === null) return 1;
      if (b.sortOrder === null) return -1;
      return a.sortOrder - b.sortOrder;
    });
    
    return configs;
  };

  // --- Get Manager Ratings for a Staff Member ---
  const getManagerRatings = async (teiId, orgUnitId) => {
    const events = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: CONFIG.stages.managerReview,
    });
    
    const ratingsMap = {};
    events.forEach(event => {
      const dataValuesMap = {};
      event.dataValues?.forEach(dv => {
        dataValuesMap[dv.dataElement] = dv.value;
      });
      
      const competencyId = dataValuesMap[CONFIG.de.competencyId];
      const period = dataValuesMap[CONFIG.de.periodType] === 'mid_year' ? 0 : 1;
      
      if (!ratingsMap[competencyId]) {
        ratingsMap[competencyId] = { managerRatings: [null, null], managerComments: ["", ""] };
      }
      
      ratingsMap[competencyId].managerRatings[period] = dataValuesMap[CONFIG.de.managerRatingScore] 
        ? parseInt(dataValuesMap[CONFIG.de.managerRatingScore]) : null;
      ratingsMap[competencyId].managerComments[period] = dataValuesMap[CONFIG.de.managerRatingComment] || "";
    });
    
    return ratingsMap;
  };

  return {
    createEvent,
    getEvents,
    updateEvent,
    deleteEvent,
    createCompetencyConfig,
    updateCompetencyConfig,
    saveCompetencyConfigsBulk, // bulk save with sort order
    saveRating,
    saveManagerRating,
    saveStaffFinalization,
    saveApproval,
    getCompetencyConfigs,
    getManagerRatings,
    getStaffFinalizations,
    CONFIG
  };
}